import { z } from 'zod';
import { requiredFieldError } from '../helper';

export const declareTaxableIncomeSchema = z.record(z.number().nonnegative(requiredFieldError('Annual taxable income')));

export const verifyIncomeConsentsSchema = z.record(
  z.literal(true, { errorMap: () => ({ message: 'Consent is required to continue' }) }),
);

export const verifyIncomeStatusSchema = z.record(
  z.literal(true, { errorMap: () => ({ message: 'Document verification must be completed before continuing' }) }),
);

// react-hook-form (zodResolver) form schemas for the LandTitle steps. These are built
// per-owner from the owner ids so every owner's field is *required* — `z.record` can't
// require specific keys, whereas a keyed `z.object` fails when an owner's value is missing.
const incomeAmountError = requiredFieldError('Annual taxable income');
const incomeTypeError = requiredFieldError('Income type');
const validIncomeError = 'Enter a valid income';

// Income types that must declare a POSITIVE income ($0 is not valid). Mirrors the values in
// apps/application-form/src/pages/LandTitle/incomeTypes.ts. Pensioner may declare $0 (verified
// via a $0 NOA) or leave the amount blank, and "No income" is forced to $0, so neither is
// included here — these positive types are the only ones that require an amount.
const POSITIVE_INCOME_TYPES = ['EMPLOYED', 'SELF_EMPLOYED', 'RETIREE'];

// "Declare taxable income" step: per-owner annual income + income type.
export const buildDeclareTaxableIncomeFormSchema = (ownerIds: string[]) =>
  z
    .object({
      // The amount is optional at the base so a Pensioner (or "No income") owner can leave it
      // blank; the positive-income requirement for the types above is enforced in superRefine.
      declaredIncomes: z.object(
        Object.fromEntries(
          ownerIds.map(id => [
            id,
            z
              .number({ required_error: incomeAmountError, invalid_type_error: incomeAmountError })
              .nonnegative(incomeAmountError)
              .optional(),
          ]),
        ),
      ),
      incomeTypes: z.object(
        Object.fromEntries(
          ownerIds.map(id => [id, z.string({ required_error: incomeTypeError }).min(1, incomeTypeError)]),
        ),
      ),
    })
    .superRefine((data, ctx) => {
      // Employed / self-employed / self-funded retiree must declare a positive income.
      for (const id of ownerIds) {
        const type = data.incomeTypes?.[id];
        const amount = data.declaredIncomes?.[id];
        if (type && POSITIVE_INCOME_TYPES.includes(type) && (amount == null || amount <= 0)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['declaredIncomes', id], message: validIncomeError });
        }
      }
    });

export type DeclareTaxableIncomeFormType = {
  // Optional per owner: Pensioner / No income owners may leave the amount blank (undefined).
  declaredIncomes: Record<string, number | undefined>;
  incomeTypes: Record<string, string>;
};

export const landTitleEligibilityCheckSchema = z.object({
  declaredIncomes: declareTaxableIncomeSchema,
  consents: verifyIncomeConsentsSchema,
  verificationDone: verifyIncomeStatusSchema,
});

export type LandTitleEligibilityCheckType = z.infer<typeof landTitleEligibilityCheckSchema>;
